import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Badge,
  Chip,
  Avatar,
  Divider,
  Button,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Event as NewAppointmentIcon,
  EventRepeat as RescheduledIcon,
  EventBusy as CancelledIcon,
  Warning as EmergencyIcon,
  MedicalServices as FollowUpIcon,
  Done as DoneIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckAllIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  ArrowForward as ArrowIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  PriorityHigh as UrgentIcon,
} from '@mui/icons-material';
import { RootState } from '../../store';
import axios from 'axios';

interface Notification {
  id: number;
  type: string;
  typeName: string;
  typeNameHindi: string;
  title: string;
  message: string;
  referenceType: string;
  referenceId: number;
  priority: string;
  priorityName: string;
  priorityNameHindi: string;
  isRead: boolean;
  readAt: string;
  createdAt: string;
  actionUrl: string;
  actionText: string;
  patientName: string;
  appointmentId: number;
  appointmentTime: string;
  timeAgo: string;
}

interface NotificationSummary {
  unreadCount: number;
  newAppointments: number;
  emergencies: number;
  followUps: number;
  todayCount: number;
}

interface NotificationListResponse {
  notifications: Notification[];
  totalNotifications: number;
  unreadCount: number;
  currentPage: number;
  totalPages: number;
}

const TAB_ALL = 0;
const TAB_UNREAD = 1;
const TAB_APPOINTMENTS = 2;
const TAB_EMERGENCIES = 3;

/**
 * Doctor Notification Center Page
 * Display and manage all notifications
 */
const DoctorNotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);

  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [summary, setSummary] = useState<NotificationSummary | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [currentTab, setCurrentTab] = useState(TAB_ALL);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'GOVERNMENT_DOCTOR') {
      navigate('/login');
      return;
    }
    fetchSummary();
    fetchNotifications();
  }, [isAuthenticated, user, navigate, currentTab]);

  const fetchSummary = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/doctor/notifications/summary`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSummary(response.data.data);
        setUnreadCount(response.data.data.unreadCount);
      }
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };

  const fetchNotifications = async (pageNum = 0) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      let endpoint = `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/doctor/notifications`;

      if (currentTab === TAB_UNREAD) {
        endpoint = `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/doctor/notifications/unread`;
      }

      const response = await axios.get(
        `${endpoint}?page=${pageNum}&size=20`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const data = response.data.data;
        if (currentTab === TAB_UNREAD) {
          setNotifications(data); // Unread returns array directly
        } else {
          if (pageNum === 0) {
            setNotifications(data.notifications);
          } else {
            setNotifications(prev => [...prev, ...data.notifications]);
          }
          setHasMore(data.currentPage < data.totalPages - 1);
        }
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId: number) => {
    try {
      const token = localStorage.getItem('accessToken');
      await axios.put(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/doctor/notifications/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      setSuccessMessage('Notification marked as read');
      fetchSummary();
    } catch (error) {
      setErrorMessage('Failed to mark notification as read');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.put(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/doctor/notifications/read-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        setUnreadCount(0);
        setSuccessMessage(`All notifications marked as read`);
        fetchSummary();
      }
    } catch (error) {
      setErrorMessage('Failed to mark all as read');
    }
  };

  const handleDelete = async (notificationId: number) => {
    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/doctor/notifications/${notificationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setSuccessMessage('Notification deleted');
      fetchSummary();
    } catch (error) {
      setErrorMessage('Failed to delete notification');
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read first
    if (!notification.isRead) {
      handleMarkAsRead(notification.id);
    }

    // Navigate to action URL if available
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'NEW_APPOINTMENT': return <NewAppointmentIcon />;
      case 'APPOINTMENT_RESCHEDULED': return <RescheduledIcon />;
      case 'APPOINTMENT_CANCELLED': return <CancelledIcon />;
      case 'EMERGENCY_REQUEST': return <EmergencyIcon />;
      case 'FOLLOW_UP_DUE': return <FollowUpIcon />;
      default: return <NotificationsIcon />;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'NEW_APPOINTMENT': return '#2563eb';
      case 'APPOINTMENT_RESCHEDULED': return '#f59e0b';
      case 'APPOINTMENT_CANCELLED': return '#ef4444';
      case 'EMERGENCY_REQUEST': return '#dc2626';
      case 'FOLLOW_UP_DUE': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'URGENT': return <ErrorIcon color="error" />;
      case 'HIGH': return <WarningIcon color="warning" />;
      default: return null;
    }
  };

  const getPriorityBgColor = (priority: string) => {
    switch (priority) {
      case 'URGENT': return '#fef2f2';
      case 'HIGH': return '#fffbeb';
      default: return 'transparent';
    }
  };

  const filterNotifications = () => {
    if (currentTab === TAB_UNREAD) {
      return notifications.filter(n => !n.isRead);
    }
    if (currentTab === TAB_APPOINTMENTS) {
      return notifications.filter(n =>
        ['NEW_APPOINTMENT', 'APPOINTMENT_RESCHEDULED', 'APPOINTMENT_CANCELLED'].includes(n.type)
      );
    }
    if (currentTab === TAB_EMERGENCIES) {
      return notifications.filter(n => n.type === 'EMERGENCY_REQUEST' || n.priority === 'URGENT');
    }
    return notifications;
  };

  const filteredNotifications = filterNotifications();

  if (loading && notifications.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: 'transparent' , minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsIcon color="primary" />
            Notification Center
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Stay updated with appointments, emergencies, and follow-ups
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => { fetchSummary(); fetchNotifications(0); }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<CheckAllIcon />}
            onClick={handleMarkAllAsRead}
            disabled={unreadCount === 0}
          >
            Mark All Read ({unreadCount})
          </Button>
        </Box>
      </Box>

      {/* Messages */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      )}

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={2.4}>
            <Card sx={{ borderRadius: 2, bgcolor: 'transparent' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Badge badgeContent={summary.unreadCount} color="error">
                  <NotificationsIcon color="primary" sx={{ fontSize: 32 }} />
                </Badge>
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>{summary.unreadCount}</Typography>
                <Typography variant="body2" color="text.secondary">Unread</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={2.4}>
            <Card sx={{ borderRadius: 2, bgcolor: 'transparent' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <NewAppointmentIcon sx={{ fontSize: 32, color: '#16a34a' }} />
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>{summary.newAppointments}</Typography>
                <Typography variant="body2" color="text.secondary">New Appointments</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={2.4}>
            <Card sx={{ borderRadius: 2, bgcolor: 'transparent' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <EmergencyIcon sx={{ fontSize: 32, color: '#dc2626' }} />
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>{summary.emergencies}</Typography>
                <Typography variant="body2" color="text.secondary">Emergencies</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={2.4}>
            <Card sx={{ borderRadius: 2, bgcolor: 'transparent' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <FollowUpIcon sx={{ fontSize: 32, color: '#7c3aed' }} />
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>{summary.followUps}</Typography>
                <Typography variant="body2" color="text.secondary">Follow-Ups</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={2.4}>
            <Card sx={{ borderRadius: 2, bgcolor: 'transparent' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <TimeIcon sx={{ fontSize: 32, color: '#d97706' }} />
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>{summary.todayCount}</Typography>
                <Typography variant="body2" color="text.secondary">Today</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ borderRadius: 2, mb: 2 }}>
        <Tabs
          value={currentTab}
          onChange={(e, v) => { setCurrentTab(v); setPage(0); }}
          variant="fullWidth"
        >
          <Tab label={`All (${notifications.length})`} />
          <Tab label={`Unread (${unreadCount})`} />
          <Tab label="Appointments" />
          <Tab label="Emergencies" />
        </Tabs>
      </Paper>

      {/* Notifications List */}
      <Paper sx={{ borderRadius: 2 }}>
        {filteredNotifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <NotificationsIcon sx={{ fontSize: 64, color: '#e5e7eb', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">No notifications</Typography>
            <Typography variant="body2" color="text.secondary">
              {currentTab === TAB_UNREAD ? 'All caught up!' : 'Notifications will appear here'}
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {filteredNotifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  disablePadding
                  sx={{
                    bgcolor: notification.isRead ? 'inherit' : getPriorityBgColor(notification.priority),
                    opacity: notification.isRead ? 0.7 : 1,
                    borderLeft: notification.isRead ? 'none' : `4px solid ${getNotificationColor(notification.type)}`,
                  }}
                >
                  <ListItemButton onClick={() => handleNotificationClick(notification)}>
                    <ListItemIcon>
                      <Avatar
                        sx={{
                          bgcolor: `${getNotificationColor(notification.type)}20`,
                          color: getNotificationColor(notification.type),
                        }}
                      >
                        {getNotificationIcon(notification.type)}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: notification.isRead ? 400 : 600 }}>
                            {notification.title}
                          </Typography>
                          {getPriorityIcon(notification.priority)}
                          {notification.priority === 'URGENT' && (
                            <Chip label="URGENT" size="small" color="error" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {notification.message}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                            {notification.patientName && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PersonIcon fontSize="small" color="action" />
                                <Typography variant="caption">{notification.patientName}</Typography>
                              </Box>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <TimeIcon fontSize="small" color="action" />
                              <Typography variant="caption">{notification.timeAgo}</Typography>
                            </Box>
                          </Box>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {!notification.isRead && (
                          <Tooltip title="Mark as read">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleMarkAsRead(notification.id); }}>
                              <DoneIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(notification.id); }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItemButton>
                </ListItem>
                {index < filteredNotifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Load More */}
      {hasMore && currentTab !== TAB_UNREAD && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              const nextPage = page + 1;
              setPage(nextPage);
              fetchNotifications(nextPage);
            }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Load More'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default DoctorNotificationsPage;
